/** Coordinate one audio element; cancelled and superseded play promises cannot change current UI. */
export function createAudioPlaybackController(audio, { onState, onError, timeoutMs = 30000 }) {
  let epoch = 0;
  let track = null;
  let phase = 'idle';
  let desired = false;
  let disposed = false;
  let timer;

  const clearTimer = () => { clearTimeout(timer); timer = undefined; };
  const publish = next => {
    if (disposed || next === phase) return;
    phase = next;
    onState({ id: track?.id || '', phase });
  };
  const fail = error => {
    if (disposed || !desired) return;
    epoch++;
    desired = false;
    clearTimer();
    audio.pause();
    publish('error');
    onError(error);
  };
  const loading = () => {
    if (disposed || !desired) return;
    publish('loading');
    if (!timer) timer = setTimeout(() => fail(new Error('Audio playback timed out')), timeoutMs);
  };
  const playing = () => {
    if (disposed || !desired) return;
    clearTimer();
    publish('playing');
  };
  const mediaError = () => fail(audio.error || new Error('Audio unavailable'));
  const nativePause = () => {
    if (!disposed && desired && audio.paused && (phase === 'loading' || phase === 'playing')) {
      epoch++;
      desired = false;
      clearTimer();
      publish('paused');
    }
  };
  audio.addEventListener('waiting', loading);
  audio.addEventListener('playing', playing);
  audio.addEventListener('error', mediaError);
  audio.addEventListener('pause', nativePause);

  const pause = () => {
    epoch++;
    desired = false;
    clearTimer();
    audio.pause();
    publish('paused');
  };
  const play = nextTrack => {
    if (disposed) return;
    if (desired && track?.id === nextTrack.id && track?.url === nextTrack.url) return;
    epoch++;
    const requestEpoch = epoch;
    clearTimer();
    const changed = track?.id !== nextTrack.id || track?.url !== nextTrack.url;
    const needsReload = changed || phase === 'error';
    track = nextTrack;
    desired = true;
    if (needsReload) {
      phase = 'idle';
      audio.pause();
      audio.src = track.url || '';
      audio.load();
    }
    if (!desired || disposed) return;
    loading();
    if (!track.id || !track.url) { fail(new Error('Audio source missing')); return; }
    try {
      Promise.resolve(audio.play()).then(() => {
        if (!disposed && epoch === requestEpoch && desired) playing();
      }, error => {
        if (disposed || epoch !== requestEpoch) return;
        if (error?.name === 'AbortError') pause();
        else fail(error);
      });
    } catch (error) { fail(error); }
  };
  const dispose = () => {
    pause();
    disposed = true;
    audio.removeEventListener('waiting', loading);
    audio.removeEventListener('playing', playing);
    audio.removeEventListener('error', mediaError);
    audio.removeEventListener('pause', nativePause);
  };
  return { play, pause, dispose };
}
